// ============================================================================
// S16 Ravo Connect — Shopify control-plane service layer.
//
// Thin axios wrappers over the FROZEN Gate-2b dashboard-facing API
// (S-16-Gate2b-Control-Plane-Contract §7). All paths are company-scoped
// ({company_id} first) exactly as the contract normalizes them.
//
// GATE-7 NOTE: OAuth / provider responses are MOCKED. There is no live Shopify
// call from the dashboard. `startInstall` returns a signed install URL from
// ai-back (mocked in tests); the browser consent + server callback are
// simulated. No token ever transits these responses — and as a defence in
// depth every response is passed through `stripCredentials` so a leaked
// secret-shaped field never reaches the caller / store / state.
// ============================================================================
import Api from "./Api";
import { stripCredentials } from "@/utils/helpers/shopify";

// Wrap a promise so the resolved payload is credential-stripped before it can
// be stored anywhere. Rejections pass through untouched (handled by stores).
function safe(promise) {
  return promise.then((res) => stripCredentials(res));
}

const base = (companyId) => `/integrations/${companyId}`;

export default {
  // 1. POST install — request a signed install URL for a canonical shop domain.
  //    Returns { install_url, state } (state is a non-secret one-time handle).
  startInstall(companyId, payload) {
    return safe(Api().post(`${base(companyId)}/shopify/install`, payload));
  },

  // S16 R12 (DTO §5 #3): NO FE callback writer in production. The Shopify OAuth
  // callback is a server-to-server GET from Shopify to ai-back (the browser is
  // redirected to install_url and never carries a token or drives the callback), so
  // the dashboard service intentionally exposes no completeCallback method.

  // 3. GET list integrations (company scope).
  list(companyId) {
    return safe(Api().get(`${base(companyId)}`));
  },

  // 4. GET one integration.
  get(companyId, integrationId) {
    return safe(Api().get(`${base(companyId)}/${integrationId}`));
  },

  // 5. GET provider locations for an integration.
  getLocations(companyId, integrationId) {
    return safe(Api().get(`${base(companyId)}/${integrationId}/locations`));
  },

  // 6. POST discover locations.
  discoverLocations(companyId, integrationId, payload = {}) {
    return safe(Api().post(`${base(companyId)}/${integrationId}/locations/discover`, payload));
  },

  // 7. PUT location mappings (link / create / ignore, with version).
  upsertMappings(companyId, integrationId, payload) {
    return safe(Api().put(`${base(companyId)}/${integrationId}/location-mappings`, payload));
  },

  // 8. PUT projection settings (scope).
  updateProjectionSettings(companyId, integrationId, payload) {
    return safe(Api().put(`${base(companyId)}/${integrationId}/projection-settings`, payload));
  },

  // 9. POST preview (family/variant/image counts + warnings).
  preview(companyId, integrationId, payload = {}) {
    return safe(Api().post(`${base(companyId)}/${integrationId}/preview`, payload));
  },

  // 10. POST sync (start initial / full sync).
  startSync(companyId, integrationId, payload = {}) {
    return safe(Api().post(`${base(companyId)}/${integrationId}/sync`, payload));
  },

  // 11. POST reconcile.
  reconcile(companyId, integrationId, payload = {}) {
    return safe(Api().post(`${base(companyId)}/${integrationId}/reconcile`, payload));
  },

  // 12. GET runs (paginated).
  getRuns(companyId, integrationId, params = {}) {
    return safe(Api().get(`${base(companyId)}/${integrationId}/runs`, { params }));
  },

  // 13. GET one run (with per_site).
  getRun(companyId, integrationId, runId) {
    return safe(Api().get(`${base(companyId)}/${integrationId}/runs/${runId}`));
  },

  // 14. POST retry a run.
  retryRun(companyId, integrationId, runId, payload = {}) {
    return safe(Api().post(`${base(companyId)}/${integrationId}/runs/${runId}/retry`, payload));
  },

  // 15. POST pause.
  pause(companyId, integrationId, payload = {}) {
    return safe(Api().post(`${base(companyId)}/${integrationId}/pause`, payload));
  },

  // 16. DELETE (disconnect, requires confirm:true).
  disconnect(companyId, integrationId, payload) {
    return safe(Api().delete(`${base(companyId)}/${integrationId}`, { data: payload }));
  },

  // Reauthorize reuses the install endpoint for a fresh signed URL (DTO §4/§5 reauth
  // route). The ONLY mounted install route is /integrations/{company_id}/shopify/install
  // (ai-back routes/api.php). The old /{integration_id}/shopify/install path did NOT
  // exist and 404'd — the server re-finds the integration by (company, shop).
  // eslint-disable-next-line no-unused-vars
  startReauthorize(companyId, integrationId, payload) {
    return safe(Api().post(`${base(companyId)}/shopify/install`, payload));
  },
};
